import React from 'react';

import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EastIcon from '@mui/icons-material/East';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import { Box, Button, Container, Typography } from '@mui/material';
import { Link } from 'react-router-dom';

import { textLinkSx } from './CaseStudies';
import PageHero from '../components/stitch/PageHero';
import Section, { cardSx } from '../components/stitch/Section';
import { tokens } from '../theme';

// Built to the Stitch "CMS Governance" screen (project 1587770953174176333), with the site's
// own copy. The hero illustration is removed; the navy panel carries the blueprint.

const patternIcons = [AccountTreeOutlinedIcon, FactCheckOutlinedIcon, HubOutlinedIcon];

// The visual stage strip repeats the figure caption, so it is hidden from assistive technology.
const stages = [
  'Central rules',
  'Site workspaces',
  'Editorial review',
  'Templates and connected systems',
];

const deliveredPatterns = [
  {
    title: 'Shared publishing, distinct sites',
    detail:
      'Earlier work used one CMS for separately branded sites, with site-specific navigation, content, and scoped editorial access. Staff could prepare content in English, Spanish, and French.',
  },
  {
    title: 'Structured content and review',
    detail:
      'Other delivered systems let administrators define fields and layouts, edit authenticated content, and move administrative briefings through department, division, and executive approval levels.',
  },
  {
    title: 'Content connected to operations',
    detail:
      'For The Garden Marquee, Scaled Tech built staff editing for venue pages, posts, galleries, and FAQs alongside inquiry and availability workflows.',
  },
];

const architecture = [
  {
    title: 'Who can edit?',
    detail:
      'Central rules could define content types, role boundaries, templates, and integration rules so people have access suited to their responsibilities.',
  },
  {
    title: 'What can vary between sites?',
    detail:
      'Site workspaces could give each site its own identity, navigation, local content, and language variants within the shared rules.',
  },
  {
    title: 'Who approves?',
    detail:
      'An editorial path could let an editor save a draft, request review, receive approval, and publish. The organization would agree on its stages and permissions.',
  },
  {
    title: 'Who owns upkeep?',
    detail:
      'Delivery and upkeep could include accessible page templates, agreed data connections, and assigned responsibilities for ongoing content review and maintenance.',
  },
];

function CmsGovernance() {
  return (
    <Container sx={{ '& .MuiTypography-root': { overflowWrap: 'anywhere' } }}>
      <PageHero
        eyebrow='CMS governance'
        title='How a governed CMS can work'
        intro='Who can edit content? Who approves it? What can each site change, and who keeps it up to date? A governed CMS can help teams answer these questions while giving each site room to serve its audience. The delivered patterns below come from separate projects; the blueprint shows how they might be combined in a future project.'
      />

      <Section
        id='delivered-patterns'
        label='Delivered foundations'
        title='Patterns from delivered work'
        tone='low'
        sx={{ mt: 0 }}
      >
        <Box
          sx={{
            mt: 4,
            display: 'grid',
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 3,
          }}
        >
          {deliveredPatterns.map((pattern, index) => {
            const Icon = patternIcons[index];
            return (
              <Box
                component='article'
                key={pattern.title}
                sx={{ ...cardSx, p: { xs: 3, md: 4 }, minWidth: 0 }}
              >
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    variant='labelCaps'
                    component='p'
                    aria-hidden='true'
                    sx={{
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: tokens.surfaceContainer,
                      color: tokens.accentText,
                    }}
                  >
                    Pattern {String(index + 1).padStart(2, '0')}
                  </Typography>
                  <Icon
                    aria-hidden='true'
                    sx={{ fontSize: 20, color: tokens.accentText }}
                  />
                </Box>
                <Typography
                  component='h3'
                  variant='h4'
                  sx={{ mt: 2.5 }}
                >
                  {pattern.title}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mt: 1.5 }}
                >
                  {pattern.detail}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Section>

      <Box
        component='section'
        aria-labelledby='reference-architecture'
        sx={{
          mt: { xs: 6, md: 10 },
          p: { xs: 3, md: 6 },
          borderRadius: 3,
          bgcolor: tokens.navy,
          color: '#FFFFFF',
          backgroundImage:
            'radial-gradient(36rem 18rem at 85% 10%, rgba(76, 214, 251, 0.10), transparent 70%)',
        }}
      >
        <Box sx={{ maxWidth: '48rem' }}>
          <Typography
            variant='labelCaps'
            component='p'
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: 'rgba(76, 214, 251, 0.06)',
              color: tokens.cyan,
            }}
          >
            Illustrative proposal, not one delivered platform
          </Typography>
          <Typography
            id='reference-architecture'
            component='h2'
            variant='h2'
            sx={{ mt: 2, color: '#FFFFFF' }}
          >
            Reference Architecture / Proprietary Platform Blueprint
          </Typography>
          <Typography
            variant='body1'
            sx={{ mt: 2, color: tokens.onNavyMuted }}
          >
            This conceptual flow combines capabilities demonstrated in different projects. It is a
            starting point for discovery, not a claim that this complete platform has been deployed.
            Roles, stages, integrations, and accessibility checks would be designed for the buyer’s
            actual governance and content needs.
          </Typography>
        </Box>

        <Box
          component='figure'
          aria-labelledby='reference-architecture architecture-caption'
          sx={{ m: 0, mt: { xs: 4, md: 5 } }}
        >
          <Box
            aria-hidden='true'
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(4, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {stages.map((stage, index) => (
              <Box
                key={stage}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                }}
              >
                <Box
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Typography
                    variant='labelCaps'
                    component='span'
                    sx={{ color: tokens.cyan }}
                  >
                    Stage {String(index + 1).padStart(2, '0')}
                  </Typography>
                  {index < stages.length - 1 && (
                    <EastIcon
                      sx={{
                        fontSize: 18,
                        color: tokens.cyan,
                        display: { xs: 'none', md: 'block' },
                      }}
                    />
                  )}
                </Box>
                <Typography
                  component='span'
                  variant='h5'
                  sx={{ display: 'block', mt: 1, color: '#FFFFFF' }}
                >
                  {stage}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            component='ol'
            sx={{
              listStyle: 'none',
              m: 0,
              mt: 3,
              p: 0,
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            {architecture.map((step) => (
              <Box
                component='li'
                key={step.title}
                sx={{
                  p: { xs: 2.5, md: 3 },
                  borderRadius: 2,
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.10)',
                  minWidth: 0,
                }}
              >
                <Typography
                  component='h3'
                  variant='h5'
                  sx={{ color: '#FFFFFF' }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mt: 1, color: tokens.onNavyMuted }}
                >
                  {step.detail}
                </Typography>
              </Box>
            ))}
          </Box>
          <Typography
            id='architecture-caption'
            component='figcaption'
            variant='bodySm'
            sx={{ display: 'block', mt: 3, color: tokens.onNavyMuted }}
          >
            Text diagram: central rules guide distinct site workspaces; editors move content through
            review; approved content reaches templates and connected systems. All steps are
            illustrative and would be tailored to a real project.
          </Typography>
        </Box>
      </Box>

      <Box
        component='section'
        aria-labelledby='capability-statement'
        sx={{
          ...cardSx,
          mt: { xs: 6, md: 8 },
          p: { xs: 3, md: 5 },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
        }}
      >
        <Box sx={{ maxWidth: '40rem' }}>
          <Typography
            id='capability-statement'
            component='h2'
            variant='h3'
          >
            CMS capability statement
          </Typography>
          <Typography
            variant='body2'
            sx={{ mt: 1.5 }}
          >
            Review a concise overview of our experience, services, and delivery approach.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            href='/cms-capabilities'
            data-track='capability_statement'
            variant='contained'
          >
            View capability statement
          </Button>
          <Button
            href='/cms-capabilities'
            data-track='capability_statement'
            download='scaled-tech-cms-capabilities.html'
            variant='outlined'
          >
            Download capability statement (HTML)
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          mt: { xs: 4, md: 6 },
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          bgcolor: tokens.surfaceLow,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box
          component={Link}
          to='/case-studies'
          sx={{ ...textLinkSx, fontSize: '1rem' }}
        >
          See delivered CMS work
          <ArrowForwardIcon
            aria-hidden='true'
            sx={{ fontSize: 20 }}
          />
        </Box>
        <Box
          component={Link}
          to='/contact-us'
          sx={{ ...textLinkSx, fontSize: '1rem' }}
        >
          Discuss your publishing workflow
          <ArrowForwardIcon
            aria-hidden='true'
            sx={{ fontSize: 20 }}
          />
        </Box>
      </Box>
    </Container>
  );
}

export default CmsGovernance;
